import { styled } from '@mui/material/styles'
import { differenceInMinutes } from 'date-fns'
import { useMemo } from 'react'
import { useDateFormat } from '../../../../../../hooks'
import { theme } from '../../../../../../modules/App/Providers/Theme/theme'
import { ResourceAvailability } from '../../../../../../types/mergedAvailabilities'
import { ResourcePair } from '../../../../../../types'
import { Spacer } from '../../../../../Spacer'
import { Title } from '../../../../../schedule/Schedule/Content/Time/Content/Availabilities/Title'
import { Item } from './Item'
import { MergedAvailabilities } from '../../../../../../types/mergedAvailabilities'
import { useTranslation } from 'react-i18next'

interface Props {
  availabilities: MergedAvailabilities
  dateStart?: Date
  timezone?: string
}

interface SlotWithEligiblePairs {
  slot: ResourceAvailability
  eligibleResourcePairs: ResourcePair[]
}

const Container = styled('div')<{
  isFitterSchedulingMode?: boolean
}>`
  align-content: start;
  color: ${theme.palette.grey[500]};
  display: grid;
  font-size: 0.75rem;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  row-gap: 1rem;
  text-align: center;

  @media (min-width: ${theme.breakpoints.values.md}px) {
    height: 17rem;
    overflow-y: auto;
  }
`

const Items = ({ availabilities, dateStart }: Props) => {
  const { t } = useTranslation()

  const { dateTo24hrDisplayTime, dateToISODateKey } = useDateFormat()
  const selectedDay = dateStart as Date
  const dayKey = dateToISODateKey(selectedDay)

  const rawSlots = availabilities?.[dayKey]

  // Group slots by time and collect all eligible resource pairs for each unique time
  const slotsWithEligiblePairs = useMemo(() => {
    if (!rawSlots?.length) return []

    const timeToSlotMap = new Map<string, SlotWithEligiblePairs>()

    rawSlots.forEach((slot) => {
      const timeKey = slot.date_start.toISOString()
      const existing = timeToSlotMap.get(timeKey)
      const isCombinedSlot = slot.resource_ids.length > 1

      // Convert resource_ids to ResourcePair tuple [fitterId, cabinId?]
      const resourcePair: ResourcePair =
        slot.resource_ids.length > 1 ? [slot.resource_ids[0], slot.resource_ids[1]] : [slot.resource_ids[0]]

      if (existing) {
        // Add this slot's resource pair to the existing eligible pairs
        existing.eligibleResourcePairs.push(resourcePair)

        // Prefer combined booking slots (with cabin) over employee-only slots
        // This ensures hasCabin check in Item component works correctly
        const existingIsCombined = existing.slot.resource_ids.length > 1
        if (isCombinedSlot && !existingIsCombined) {
          existing.slot = slot
        }
      } else {
        // First occurrence of this time - create new entry
        timeToSlotMap.set(timeKey, { slot, eligibleResourcePairs: [resourcePair] })
      }
    })

    return Array.from(timeToSlotMap.values())
  }, [rawSlots])

  const hasAvailabilitiesForSelectedDay = Boolean(slotsWithEligiblePairs?.length)

  if (!hasAvailabilitiesForSelectedDay) {
    return <Container>{t('common.label.noAvailabilitiesForThisDay')}</Container>
  }

  const TIME_SLOT_DATA_ATTRIBUTE = 'data-time-slots'

  return (
    <>
      {hasAvailabilitiesForSelectedDay && <Title />}
      <Spacer count={0.5} />
      <div {...{ [TIME_SLOT_DATA_ATTRIBUTE]: true }} style={{ position: 'relative', height: 1, top: '4rem', zIndex: 0 }} />
      <Container as="ul">
        {slotsWithEligiblePairs?.map(({ slot, eligibleResourcePairs }) => {
          const duration = differenceInMinutes(slot.date_end, slot.date_start)
          const key = `${dateTo24hrDisplayTime(slot.date_start)}${duration}`

          return <Item slot={slot} eligibleResourcePairs={eligibleResourcePairs} key={key} />
        })}
      </Container>
    </>
  )
}

export { Items }
